import { useFlags } from '@atlaskit/flag';
import { IDelivery } from 'core/api/deliveries/deliveries-api-interface';
import { getDeliveryStatusNameFromKey } from 'core/constants/delivery-status';
import { useDialog } from 'core/providers/DialogProvider';
import dayjs from 'dayjs';
import { orderBy, where } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import SharedButton from 'shared/components/buttons/button';
import PageHeader from 'shared/components/page-header/page-header';
import SharedTable from 'shared/components/table/table';
import {
  ISharedTableColumn,
  ISharedTableCustomCellTemplate,
  ISharedTableRow,
} from 'shared/components/table/table-interface';
import { getDeliveryTypeNameFromKey } from 'core/constants/delivery-types';
import { useNavigate } from 'react-router-dom';
import { getWasteCollectionNameFromKey } from 'core/constants/waste-collection';
import { getStorageRequirementNameFromKey } from 'core/constants/storage-requirement';
import { getSharpsBinOptionNameFromKey } from 'core/constants/sharps-bin-options';
import {
  DeliveryRouteAssignment,
  getDeliveryRouteAssignmentNameFromKey,
} from 'core/constants/delivery-route-assignment';
import { getRouteOptionNameFromKey } from 'core/constants/route-options';
import { useCollection } from 'core/hooks/useCollection';
import { IDeliveryDao } from 'core/api/types/deliveries.interface';
import ConfirmationDialog from 'modules/dialogs/confirmation-dialog';
import { deleteItem } from 'core/utilities/api-wrapper';
import { IButton } from 'shared/components/buttons/button-interface';
import { DeliveriesApiService } from 'core/api';

const DeliveryTableColumns: ISharedTableColumn[] = [
  { label: 'Customer Name', key: 'customerName', templateId: 'text', width: 10 },
  { label: 'Postcode', key: 'postcode', templateId: 'text', width: 10 },
  { label: 'Parcels', key: 'parcelCount', templateId: 'text', width: 5, isCompact: true },
  { label: 'Sharps bin', key: 'sharpsBin', templateId: 'text', width: 10 },
  { label: 'Waste collection', key: 'wasteCollection', templateId: 'text', width: 5 },
  { label: 'Storage', key: 'storageRequirement', templateId: 'text', width: 10 },
  { label: 'Delivery Date', key: 'date', templateId: 'text', width: 10 },
  { label: 'Status', key: 'status', templateId: 'text', width: 10 },
  { label: 'Type', key: 'type', templateId: 'text', width: 10 },
  { label: 'Route pot', key: 'route', templateId: 'text', width: 10 },
  { label: 'Allocated route', key: 'assignedRoute', templateId: 'text', width: 10 },
  { label: '', key: 'actions', templateId: 'actions', width: 9, isCompact: true },
];

const UnallocatedDeliveriesList = () => {
  const dialog = useDialog();
  const flags = useFlags();
  const navigate = useNavigate();
  const [tableRows, setTableRows] = useState<ISharedTableRow<IDeliveryDao>[]>([]);

  const constraints = useMemo(
    () => [
      where('assignedRoute', '==', DeliveryRouteAssignment.NONE),
      where('date', '<=', dayjs().format('YYYY-MM-DD')),
      orderBy('date', 'desc'),
      orderBy('address.town_or_city'),
    ],
    [],
  );

  const { data, loading, loadMore, hasMore, loadingMore } = useCollection<IDeliveryDao>({
    apiService: DeliveriesApiService,
    constraints,
  });

  useEffect(() => {
    if (data) {
      setTableRows(
        data.map((data) => ({
          key: data.uid,
          data: {
            ...data,
            customerName: `${data.firstName} ${data.lastName}`,
            date: dayjs(data.date).format('DD/MM/YYYY'),
            type: getDeliveryTypeNameFromKey(data.type),
            postcode: data.address.postcode,
            status: getDeliveryStatusNameFromKey(data.status),
            wasteCollection: getWasteCollectionNameFromKey(data.wasteCollection),
            storageRequirement: getStorageRequirementNameFromKey(data.storageRequirement),
            sharpsBin: getSharpsBinOptionNameFromKey(data.sharpsBin),
            route: getDeliveryRouteAssignmentNameFromKey(data.route),
            assignedRoute: getAllocatedRoute(data.assignedRoute),
          },
        })),
      );
    }
  }, [data]);

  const getAllocatedRoute = (key?: string) => {
    if (key === DeliveryRouteAssignment.NONE) {
      return 'Unallocated';
    }
    return key ? getRouteOptionNameFromKey(key) : 'Unknown';
  };

  const actionCellTemplate = (delivery: IDelivery) => {
    return (
      <div className='w-full flex justify-end'>
        <SharedButton
          onClick={() => navigate(`/deliveries/${delivery.uid}`)}
          type='button'
          appearance='subtle'
          spacing='compact'
          label='View'
        />
        <SharedButton
          onClick={() =>
            dialog?.openDialog(
              <ConfirmationDialog
                title='Delete delivery'
                textContent='Are you sure you want to delete this delivery? This action cannot be undone.'
                action={() => deleteItem(DeliveriesApiService, delivery, flags, dialog)}
                buttonLabel='Delete Delivery'
              />,
            )
          }
          type='button'
          appearance='subtle'
          spacing='compact'
          label='Delete'
        />
      </div>
    );
  };

  const customTemplates: ISharedTableCustomCellTemplate[] = [
    {
      template: actionCellTemplate,
      id: 'actions',
    },
  ];

  const loadMoreButton: IButton = {
    onClick: () => loadMore(),
    type: 'submit',
    appearance: 'subtle',
    label: 'Load more',
    isLoading: loadingMore,
  };

  return (
    <>
      <PageHeader title='Unallocated Deliveries' />
      <div className='mt-4'>
        <SharedTable
          columns={DeliveryTableColumns}
          rows={tableRows}
          isLoading={loading}
          customTemplates={customTemplates}
          loadMoreButton={hasMore ? loadMoreButton : undefined}
        />
      </div>
    </>
  );
};

export default UnallocatedDeliveriesList;
